import React, { useEffect, useState, useRef } from 'react';
import { XIcon, ClockIcon, UserIcon, SendIcon, AtSignIcon, PaperclipIcon } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { ticketComments, currentUser } from '../data/mockData';
interface TicketDetailModalProps {
  ticket: any;
  onClose: () => void;
}
export function TicketDetailModal({
  ticket,
  onClose
}: TicketDetailModalProps) {
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState<any[]>(ticketComments[ticket.id] || []);
  const [showMentions, setShowMentions] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  // Scroll to bottom of comments on load or when new comment added
  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [comments]);
  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setNewComment(val);
    // Simple mention detection: if user types '@' at the end or after a space
    if (val.endsWith('@') || val.endsWith(' @')) {
      setShowMentions(true);
    } else if (!val.includes('@')) {
      setShowMentions(false);
    }
  };
  const handleMentionSelect = (name: string) => {
    const beforeAt = newComment.substring(0, newComment.lastIndexOf('@'));
    setNewComment(`${beforeAt}@${name} `);
    setShowMentions(false);
    if (commentInputRef.current) {
      commentInputRef.current.focus();
    }
  };
  const handleSubmitComment = () => {
    if (!newComment.trim()) return;
    const comment = {
      id: `c-new-${Date.now()}`,
      ticketId: ticket.id,
      author: currentUser.name,
      authorInitials: currentUser.initials,
      authorRole: currentUser.role,
      content: newComment,
      timestamp: 'Just now',
      isInternal: false
    };
    setComments([...comments, comment]);
    setNewComment('');
    setShowMentions(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-160">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-161"></div>

      {/* Modal Content */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-162">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-100 bg-slate-50/50" data-id="element-163">
          <div data-id="element-164">
            <div className="flex items-center space-x-3 mb-2" data-id="element-165">
              <span className="font-mono text-sm font-bold text-slate-500" data-id="element-166">
                {ticket.id}
              </span>
              <StatusBadge type="status" value={ticket.status} data-id="element-167" />
              <StatusBadge type="priority" value={ticket.priority} data-id="element-168" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 pr-8" data-id="element-169">
              {ticket.subject}
            </h2>
            <div className="flex items-center text-sm text-slate-500 mt-2 space-x-4" data-id="element-170">
              <span className="flex items-center" data-id="element-171">
                <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-172" /> Submitted{' '}
                {ticket.submittedDate}
              </span>
              <span className="flex items-center" data-id="element-173">
                <UserIcon className="w-4 h-4 mr-1.5" data-id="element-174" />{' '}
                {ticket.assignedTo ? `Assigned to ${ticket.assignedTo}` : 'Unassigned'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-175">
            <XIcon className="w-6 h-6" data-id="element-176" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-slate-100 bg-white" data-id="element-177">
          <div className="flex items-center justify-between mb-2" data-id="element-178">
            <span className="text-sm font-medium text-slate-700" data-id="element-179">
              Resolution Progress
            </span>
            <span className="text-sm font-medium text-slate-500" data-id="element-180">
              {ticket.progress}%
            </span>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-181">
            <div className={`h-full rounded-full transition-all duration-500 ${ticket.status === 'Resolved' ? 'bg-green-500' : ticket.status === 'New' ? 'bg-blue-500' : 'bg-amber-500'}`} style={{
            width: `${ticket.progress}%`
          }} data-id="element-182"></div>
          </div>
        </div>

        {/* Body (Scrollable) */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/30" data-id="element-183">
          {/* Original Description */}
          <div className="mb-8 bg-white p-5 rounded-xl border border-slate-200 shadow-sm" data-id="element-184">
            <h3 className="text-sm font-bold text-slate-900 mb-2" data-id="element-185">
              Original Request
            </h3>
            <p className="text-slate-700 whitespace-pre-wrap" data-id="element-186">
              {ticket.description}
            </p>
          </div>

          {/* Comments Thread */}
          <div className="space-y-6" data-id="element-187">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-188">Activity</h3>

            {comments.map((comment, index) => {
            const isCurrentUser = comment.author === currentUser.name;
            const isSystem = comment.author === 'System';
            if (isSystem) {
              return <div key={comment.id} className="flex justify-center my-4" data-id="element-189">
                    <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs font-medium rounded-full" data-id="element-190">
                      {comment.content} • {comment.timestamp}
                    </span>
                  </div>;
            }
            return <div key={comment.id} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`} data-id="element-191">
                  <div className={`flex max-w-[85%] ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-192">
                    {/* Avatar */}
                    <div className={`flex-shrink-0 ${isCurrentUser ? 'ml-3' : 'mr-3'}`} data-id="element-193">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-white shadow-sm ${isCurrentUser ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-194">
                        {comment.authorInitials}
                      </div>
                    </div>

                    {/* Message Bubble */}
                    <div className={`flex flex-col ${isCurrentUser ? 'items-end' : 'items-start'}`} data-id="element-195">
                      <div className="flex items-baseline space-x-2 mb-1" data-id="element-196">
                        <span className="text-sm font-medium text-slate-900" data-id="element-197">
                          {comment.author}
                        </span>
                        <span className="text-xs text-slate-500" data-id="element-198">
                          {comment.timestamp}
                        </span>
                      </div>

                      <div className={`p-4 rounded-2xl text-sm ${isCurrentUser ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none shadow-sm'}`} data-id="element-199">
                        {/* Render mentions with styling */}
                        {comment.content.split(/(@[\w\s]+)/g).map((part: string, i: number) => {
                      if (part.startsWith('@')) {
                        return <span key={i} className={`font-semibold ${isCurrentUser ? 'text-blue-200' : 'text-blue-600'}`} data-id="element-200">
                                  {part}
                                </span>;
                      }
                      return <span key={i} data-id="element-201">{part}</span>;
                    })}
                      </div>
                    </div>
                  </div>
                </div>;
          })}
            <div ref={commentsEndRef} data-id="element-202" />
          </div>
        </div>

        {/* Comment Input Area */}
        {ticket.status !== 'Resolved' && <div className="p-4 sm:p-6 border-t border-slate-200 bg-white relative" data-id="element-203">
            {/* Mentions Popover */}
            {showMentions && ticket.assignedTo && <div className="absolute bottom-full left-6 mb-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-10 animate-in slide-in-from-bottom-2" data-id="element-204">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500" data-id="element-205">
                  Tag assigned user
                </div>
                <button className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center space-x-3 transition-colors" onClick={() => handleMentionSelect(ticket.assignedTo)} data-id="element-206">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold" data-id="element-207">
                    {ticket.assignedTo.split(' ').map((n: string) => n[0]).join('')}
                  </div>
                  <div data-id="element-208">
                    <div className="text-sm font-medium text-slate-900" data-id="element-209">
                      {ticket.assignedTo}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-210">IT Support</div>
                  </div>
                </button>
              </div>}

            <div className="flex items-end space-x-3" data-id="element-211">
              <div className="flex-shrink-0 mb-1" data-id="element-212">
                <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold ring-2 ring-white shadow-sm" data-id="element-213">
                  {currentUser.initials}
                </div>
              </div>

              <div className="flex-1 relative" data-id="element-214">
                <textarea ref={commentInputRef} value={newComment} onChange={handleCommentChange} onKeyDown={handleKeyDown} placeholder="Type a message... Use @ to tag the assigned user" className="w-full pl-4 pr-12 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none resize-none min-h-[52px] max-h-32 text-sm" rows={1} style={{
              height: 'auto'
            }} data-id="element-215" />
                <div className="absolute right-2 bottom-2 flex space-x-1" data-id="element-216">
                  <button className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg transition-colors" title="Attach file" data-id="element-217">
                    <PaperclipIcon className="w-4 h-4" data-id="element-218" />
                  </button>
                  <button className="p-1.5 text-slate-400 hover:text-blue-600 rounded-lg transition-colors" title="Mention user" onClick={() => {
                if (ticket.assignedTo) {
                  setNewComment(prev => prev + (prev.endsWith(' ') || prev === '' ? '@' : ' @'));
                  setShowMentions(true);
                  commentInputRef.current?.focus();
                }
              }} data-id="element-219">
                    <AtSignIcon className="w-4 h-4" data-id="element-220" />
                  </button>
                </div>
              </div>

              <button onClick={handleSubmitComment} disabled={!newComment.trim()} className="mb-1 p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0" data-id="element-221">
                <SendIcon className="w-5 h-5" data-id="element-222" />
              </button>
            </div>
            <div className="mt-2 text-xs text-slate-500 text-right pr-14" data-id="element-223">
              Press Enter to send, Shift+Enter for new line
            </div>
          </div>}

        {ticket.status === 'Resolved' && <div className="p-4 bg-green-50 border-t border-green-100 text-center" data-id="element-224">
            <p className="text-sm text-green-800 font-medium" data-id="element-225">
              This ticket has been resolved and is closed to new comments.
            </p>
          </div>}
      </div>
    </div>;
}